import React, { useCallback, useEffect, useState } from 'react';
import {
    Paper, Typography, Box, Button, TextField, Grid, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getDqtRateService, setDqtRateService } from '../../../../services/billing.service';
import { toastSuccess } from '../../../../utils/utils';

/**
 * DQTokenRateTab — the DQ Token (DQT) ↔ USD rate.
 *
 * One global rate, kept as a history: setting a rate adds a new record and never
 * edits the old one. The rate turns USD received into DQT when a payment is
 * granted. A change applies to grants made from now on only — DQT already held
 * by partners is never adjusted.
 *
 * Until a rate is set, DQT grants are refused by the API. There is no built-in
 * default.
 */

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const DQTokenRateTab = () => {
    const [loading, setLoading] = useState(true);
    const [rate, setRate] = useState(null);
    const [history, setHistory] = useState([]);
    const [newRate, setNewRate] = useState('');
    const [note, setNote] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getDqtRateService();
            setRate(res?.data?.rate || null);
            setHistory(res?.data?.history || []);
        } catch (err) {
            // invokeApi has already shown the error.
            console.error('[DQTokenRateTab] load failed:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const value = Number(newRate);
    const valid = newRate !== '' && Number.isFinite(value) && value > 0;

    const handleSave = async () => {
        if (!valid) return;
        setSaving(true);
        try {
            await setDqtRateService({ usd_per_dqt: value, note: note.trim() });
            toastSuccess(`DQT rate set to US$${value} per DQT.`);
            setNewRate('');
            setNote('');
            setConfirmOpen(false);
            await load();
        } catch (err) {
            console.error('[DQTokenRateTab] save failed:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Current rate */}
                <Grid item xs={12} md={5}>
                    <Paper style={{ padding: 24, height: '100%' }}>
                        <Typography variant="overline" color="textSecondary">Current rate</Typography>
                        {loading ? (
                            <Box mt={2}><CircularProgress size={24} /></Box>
                        ) : rate ? (
                            <>
                                <Typography variant="h4" style={{ fontWeight: 700, marginTop: 4 }}>
                                    1 DQT = US${rate.usd_per_dqt}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                    In effect since {fmtDate(rate.effective_from)}
                                    {rate.set_by?.label ? ` · set by ${rate.set_by.label}` : ''}
                                </Typography>
                                {rate.note ? (
                                    <Typography variant="body2" style={{ marginTop: 8 }}>“{rate.note}”</Typography>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" style={{ fontWeight: 700, marginTop: 4, color: '#b45309' }}>
                                    No rate set
                                </Typography>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                    DQ Tokens cannot be granted until a rate is set.
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* Set a new rate */}
                <Grid item xs={12} md={7}>
                    <Paper style={{ padding: 24, height: '100%' }}>
                        <Typography variant="overline" color="textSecondary">Set a new rate</Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                            Applies to DQ Tokens granted from now on. Balances partners already hold are not changed.
                            The previous rate stays on record.
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    label="US$ per 1 DQT"
                                    type="number"
                                    value={newRate}
                                    onChange={(e) => setNewRate(e.target.value)}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    error={newRate !== '' && !valid}
                                    helperText={newRate !== '' && !valid ? 'Must be greater than 0' : 'e.g. 1 for 1 DQT = US$1'}
                                    fullWidth variant="outlined" size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField
                                    label="Note (optional)"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    fullWidth variant="outlined" size="small"
                                    helperText="Why the rate is changing"
                                />
                            </Grid>
                        </Grid>
                        <Box mt={2}>
                            <Button variant="contained" color="primary" disabled={!valid} onClick={() => setConfirmOpen(true)}>
                                Set rate
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* History */}
            <Paper style={{ padding: 24, marginTop: 24 }}>
                <Typography variant="overline" color="textSecondary">Rate history</Typography>
                <Divider style={{ margin: '8px 0 12px' }} />
                {history.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No rates recorded yet.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>US$ per DQT</TableCell>
                                    <TableCell>Effective from</TableCell>
                                    <TableCell>Set by</TableCell>
                                    <TableCell>Note</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((r, i) => (
                                    <TableRow key={r._id || i} selected={i === 0}>
                                        <TableCell style={{ fontWeight: i === 0 ? 700 : 400 }}>{r.usd_per_dqt}</TableCell>
                                        <TableCell>{fmtDate(r.effective_from)}</TableCell>
                                        <TableCell>{r.set_by?.label || r.set_by?.email || '—'}</TableCell>
                                        <TableCell>{r.note || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Change the DQT rate?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        From now on, <strong>US${valid ? value : '—'}</strong> will buy <strong>1 DQT</strong>.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                        {rate ? `Current rate: US$${rate.usd_per_dqt} per DQT. ` : ''}
                        Balances partners already hold are not changed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
                        {saving ? 'Saving…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DQTokenRateTab;
